import type {
    AutocompleteApi,
    AutocompleteCollection,
    AutocompleteState,
    BaseItem,
} from "@algolia/autocomplete-core";
import type { AutocompletePlugin } from "@algolia/autocomplete-js";
import type { RecentSearchesPluginData } from "@algolia/autocomplete-plugin-recent-searches";
import type {
    HistoryItem,
    RecentSearchesItem,
} from "@algolia/autocomplete-plugin-recent-searches/dist/esm/types";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Spinner } from "@ecco/ui/spinner";
import { Box } from "@ecco/ui/box";
import { Grid } from "@ecco/ui/grid";
import { shadows, sizes, transitions } from "@ecco/ui/tokens";
import { RecentSearchesGrid } from "./RecentSearchesGrid";
import { QuerySuggestionsGrid } from "./QuerySuggestionsGrid";
import { ProductsGrid } from "./ProductsGrid";
import { Fragment } from "react";
import type {
    ProductsCollectionValue,
    QuerySuggestionCollectionValue,
    RecentSearchesCollectionValue,
} from "./types";
import { GRID_AREAS } from "./AutoCompletePanelStyles";

interface AutoCompletePanelProps {
    autocompleteState: Partial<AutocompleteState<BaseItem>>;
    autocomplete: AutocompleteApi<BaseItem>;
    isLoading: boolean;
    recentSearchesPlugin: AutocompletePlugin<
        RecentSearchesItem,
        RecentSearchesPluginData<RecentSearchesItem>
    >;
    searchBarOpen: boolean;
}

export const AutoCompletePanel = ({
    autocompleteState,
    autocomplete,
    isLoading,
    recentSearchesPlugin,
    searchBarOpen,
}: AutoCompletePanelProps) => {
    const { query = "", collections } = autocompleteState;
    const suggestionCollection = collections?.filter((collection) =>
        ["recentSearchesPlugin", "querySuggestionsPlugin"].includes(collection.source.sourceId)
    );
    const productCollections = collections?.filter(
        (collection) => collection.source.sourceId === "products"
    );

    const addRecentSearch = (item: HistoryItem) => {
        recentSearchesPlugin?.data?.addItem(item);
        autocomplete?.refresh();
    };
    const removeRecentSearch = (id: string) => {
        recentSearchesPlugin?.data?.removeItem(id);
        autocomplete?.refresh();
    };

    const { getMultiple } = useMicrocopy();

    const microcopies = getMultiple(CommonCMS.shared, [CommonCMS.searchBarPanelLabel]);

    const renderCollectionItem = (
        collection: AutoCompletePanelProps["autocompleteState"]["collections"][number]
    ) => {
        switch (collection?.source.sourceId) {
            case "recentSearchesPlugin":
                return (
                    !query && (
                        <RecentSearchesGrid
                            autocomplete={autocomplete}
                            collection={
                                collection as AutocompleteCollection<RecentSearchesCollectionValue>
                            }
                            onRemoveRecentSearch={(id) => removeRecentSearch(id)}
                            recentSearchesPlugin={recentSearchesPlugin}
                        />
                    )
                );
            case "querySuggestionsPlugin":
                return (
                    <QuerySuggestionsGrid
                        totalHits={autocompleteState.context?.totalHits as number}
                        autocomplete={autocomplete}
                        collection={
                            collection as AutocompleteCollection<QuerySuggestionCollectionValue>
                        }
                        query={query}
                        onResultClick={(item) => addRecentSearch(item)}
                    />
                );
            case "products":
                return (
                    <ProductsGrid
                        autocomplete={autocomplete}
                        query={query}
                        collection={collection as AutocompleteCollection<ProductsCollectionValue>}
                    />
                );
            default:
                return null;
        }
    };

    const getPanelTransition = () => {
        const duration = transitions.duration.fast;
        const easing = transitions.easing["ease-out"];
        if (searchBarOpen) {
            return `visibility 0s ${easing}, opacity ${duration}ms ${easing}`;
        }
        return `visibility 0s ${duration}ms ${easing}, opacity ${duration}ms ${easing}`;
    };

    return (
        <Box
            className="aa-Panel"
            backgroundColor={{ base: "white", lg: "whiteTransparent" }}
            backdropFilter={{ base: "none", lg: "blur(7.5px)" }}
            position={{ base: "relative", lg: "absolute" }}
            left={0}
            right={0}
            top={{ base: 0, lg: sizes.navBarCollapsedHeight }}
            marginTop="0.2"
            zIndex={55}
            width="100%"
            height={{
                base: `auto`,
                lg: sizes.navExpandedHeight,
            }}
            visibility={searchBarOpen ? "visible" : "hidden"}
            opacity={searchBarOpen ? 1 : 0}
            transition={getPanelTransition()}
            overflow="hidden"
            aria-label={microcopies[CommonCMS.searchBarPanelLabel]}
        >
            <Grid
                className="aa-PanelLayout aa-Panel--scrollable"
                gap="0.2"
                maxW="container.largeDesktop"
                mx="auto"
                gridTemplateAreas={`"${GRID_AREAS.suggestions} ${GRID_AREAS.products}"`}
                gridTemplateColumns={{ base: "1fr", lg: "320px auto", xl: "360px auto" }}
                gridTemplateRows="auto"
                height="100%"
            >
                {isLoading && (
                    <Box
                        gridArea={`${GRID_AREAS.suggestions} ${GRID_AREAS.products}`}
                        width="100%"
                        height="100%"
                        display="flex"
                        gridRow="1 / -1"
                        gridColumn="1 / -1"
                        justifyContent="center"
                        alignItems="center"
                        opacity="0.5"
                        py="10"
                    >
                        <Spinner size="xl" />
                    </Box>
                )}
                {!isLoading && (
                    <>
                        <Box
                            gridArea={GRID_AREAS.suggestions}
                            padding={{ base: 0, lg: "5" }}
                            flexDir="column"
                            display="flex"
                            gap="6"
                            marginTop={{ base: 5, lg: 0 }}
                            boxShadow={{ base: "none", lg: shadows.right }}
                        >
                            {suggestionCollection?.map((collection) => (
                                <Fragment key={`collection-${collection.source.sourceId}`}>
                                    {renderCollectionItem(collection)}
                                </Fragment>
                            ))}
                        </Box>
                        <Box gridArea={"products"} backgroundColor="white">
                            {productCollections?.map((collection) => (
                                <Fragment key={`collection-${collection.source.sourceId}`}>
                                    {renderCollectionItem(collection)}
                                </Fragment>
                            ))}
                        </Box>
                    </>
                )}
            </Grid>
        </Box>
    );
};
